import { create } from 'zustand';

/** §2.10. `order` is the current grid's placement order (keys), published by MasonryGrid. */
type SelectionState = {
  ids: Set<string>;
  anchorIndex: number | null;
  focusIndex: number;
  order: string[];
  setOrder: (order: string[]) => void;
  select: (key: string, index: number) => void;
  toggle: (key: string, index: number) => void;
  rangeTo: (index: number) => void;
  selectAll: () => void;
  addMany: (keys: Iterable<string>, replace: boolean) => void;
  clear: () => void;
  setFocus: (index: number) => void;
};

export const useSelection = create<SelectionState>((set, get) => ({
  ids: new Set(),
  anchorIndex: null,
  focusIndex: 0,
  order: [],
  setOrder: (order) =>
    set((s) => {
      // drop ids that no longer exist
      if (s.ids.size === 0) return { order };
      const present = new Set(order);
      let changed = false;
      const ids = new Set<string>();
      for (const id of s.ids) if (present.has(id)) ids.add(id);
      else changed = true;
      return changed ? { order, ids } : { order };
    }),
  select: (key, index) => set({ ids: new Set([key]), anchorIndex: index, focusIndex: index }),
  toggle: (key, index) =>
    set((s) => {
      const ids = new Set(s.ids);
      if (ids.has(key)) ids.delete(key);
      else ids.add(key);
      return { ids, anchorIndex: index, focusIndex: index };
    }),
  rangeTo: (index) =>
    set((s) => {
      const anchor = s.anchorIndex ?? index;
      const [a, b] = anchor < index ? [anchor, index] : [index, anchor];
      const ids = new Set(s.ids);
      for (let i = a; i <= b; i++) {
        const k = s.order[i];
        if (k) ids.add(k);
      }
      return { ids, focusIndex: index };
    }),
  selectAll: () => set((s) => ({ ids: new Set(s.order) })),
  addMany: (keys, replace) =>
    set((s) => {
      const ids = replace ? new Set<string>() : new Set(s.ids);
      for (const k of keys) ids.add(k);
      return { ids };
    }),
  clear: () => (get().ids.size ? set({ ids: new Set(), anchorIndex: null }) : undefined),
  setFocus: (focusIndex) => set({ focusIndex }),
}));
